import { showsActivityPrice } from "@majourneys/shared";
import { useCallback, useEffect, useState, type HTMLAttributes, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { useDisplayPriceParts } from "../../lib/currency";
import { activityHrefString } from "../../features/activities/lib/activity-href";
import { useRecentBooking } from "../../features/activities/lib/recent-booking";
import { CardImageCarousel } from "./card-image-carousel";
import { NewBadge } from "./new-badge";
import { pewterInner } from "./pewter-edge";
import { CARD_FILL, CARD_RADIUS, cardFrameStyle } from "./card-frame";
import { useSaved } from "../../features/saved/state/saved-context";
import { cardPriceUnitKey } from "../../features/activities/lib/price-unit";

import { Icon } from "@majourneys/icons";
export interface ActivityCard2Props {
  slug: string;
  locale?: string;
  image?: string;
  /**
   * The item's gallery. When it holds more than one photo the guest can swipe
   * through them on the card (dots, no arrows); `image` is used otherwise.
   */
  images?: string[];
  title: string;
  city: string;
  citySlug?: string | null;
  category?: string;
  categorySlug?: string | null;
  segment?: string | null;
  price: number;
  currency?: string;
  priceType?: string | number;
  variant?: "default" | "wishlist" | "blog";
  /**
   * Mark the first card(s) in a rail as priority so the image loads eagerly
   * with high fetchpriority. Lighthouse picks the first activity card image
   * as LCP on home/category pages — lazy loading delays it 6+ seconds.
   */
  priority?: boolean;
  /** Added in the last 30 days (decided by the API): a gold "New" pill on the photo. */
  isNew?: boolean;
}

export function ActivityCard2({
  slug,
  locale = "en",
  image,
  images,
  title,
  city,
  citySlug,
  category,
  categorySlug,
  segment,
  price,
  currency = "MAD",
  priceType = "Person",
  variant = "default",
  priority = false,
  isNew = false,
  className,
}: ActivityCard2Props & HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPriceParts();
  const display = displayPrice(price);
  // Never on a restaurant, even with a stale stored price (owner rule).
  const hasPrice = showsActivityPrice({ price, categorySlug, segment });
  // Prefer canonical 4-segment URL when we have city + category/segment.
  // Fall back to legacy 1-seg path (which 301s server-side) when caller can't
  // provide slugs — e.g. local saved-items cache in account page.
  const hasCanonicalParts = !!citySlug && (!!categorySlug || !!segment);
  const href = hasCanonicalParts
    ? activityHrefString({
        lang: locale,
        citySlug,
        categorySlug: categorySlug ?? null,
        segment: segment ?? null,
        slug,
      })
    : `/${locale}/${slug}`;
  const ctaLabel = variant === "blog" ? t("common.read_more") : t("common.book_now");
  const priceIcon = getPriceIcon(category, priceType);
  // Through the same resolver as the activity page, and translated: this was
  // a hard-coded English "Per group" on every locale, and a buggy read "per
  // group" here while its page said "per buggy".
  const priceLabel = t(cardPriceUnitKey({ category, categorySlug, slug, priceType }));
  const recentBookingRef = useRecentBooking(slug);
  const showBookedCta = variant === "default" && !!recentBookingRef;
  const shortRef =
    recentBookingRef && recentBookingRef.length > 12
      ? `${recentBookingRef.slice(0, 7)}…${recentBookingRef.slice(-4)}`
      : recentBookingRef;
  const bookingHref = recentBookingRef
    ? `/${locale}/booking/${recentBookingRef}/thank-you`
    : null;

  // One store for every heart on the site, account-backed once signed in.
  const { isSaved, toggle: toggleSaved } = useSaved();
  const saved = isSaved(slug);


  const onToggleSave = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      void toggleSaved({ slug, title, city, price, currency, priceType, image, category });
    },
    [toggleSaved, slug, title, city, price, currency, priceType, image, category],
  );

  return (
    <article
      className={cn(
        "mj-activity-card group relative mx-auto mb-5 w-[240px] max-w-[240px] transition-[transform,box-shadow] duration-200 hover:-translate-y-[3px]",
        className,
      )}
      style={cardFrameStyle}
    >
      <div className="flex h-full flex-col overflow-hidden p-2" style={pewterInner(CARD_FILL, CARD_RADIUS)}>
      <CardImageCarousel
        images={images && images.length > 0 ? images : image ? [image] : []}
        alt={title}
        arrows={false}
        priority={priority}
        className="aspect-square w-full overflow-hidden rounded-[16px]"
        imageClassName="object-center"
        renderSlide={(photo) => (
          <a href={href} className="block h-full w-full">
            {photo}
          </a>
        )}
        overlay={
          <>
            {category && (
              /* White, matching the heart beside it and the discover card's chip. */
              <span className="pointer-events-none absolute start-2.5 top-2.5 inline-flex min-h-[26px] items-center whitespace-nowrap rounded-full bg-white/95 px-3 font-sans text-[10px] font-bold uppercase leading-[14px] tracking-[0.08em] text-[#040033] shadow-[0_2px_8px_rgba(4,0,51,0.15)] backdrop-blur">
                {category}
              </span>
            )}
            {isNew && <NewBadge className="pointer-events-none absolute bottom-2.5 start-2.5" />}
            <button
              type="button"
              onClick={onToggleSave}
              aria-pressed={saved}
              aria-label={saved ? t("activity_card.unsave_aria", { title }) : t("activity_card.save_aria", { title })}
              data-testid="activity-card-save"
              className={cn(
                "absolute end-2.5 top-2.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#110E39] shadow-[0_0_0_1px_rgba(17,14,57,0.14)] transition-transform duration-150 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#110E39]/40",
                // Saved is gold, like every save heart (owner, 2026-10-13).
                saved && "text-[#E5B265]",
              )}
            >
              <Icon name={cn(
                  "text-[18px] leading-none",
                  saved ? "ri-heart-fill" : "ri-heart-line",
                )} aria-hidden="true" />
            </button>
          </>
        }
      />
      <div className="flex flex-1 flex-col gap-[5px] px-1 pb-1 pt-2.5">
        <h3 className="m-0 line-clamp-2 flex-1 font-sans text-[13px] font-bold leading-[1.3] text-[#040033]">
          <a href={href} className="text-[#040033] no-underline hover:text-[#040033]">
            {title}
          </a>
        </h3>
        {/* A hairline sets the title off from the city and price, the way the
            sign-in sheet separates its own blocks. */}
        <div className="h-px bg-[rgba(4,0,51,0.09)]" />
        <div className="flex flex-row flex-wrap items-center justify-between gap-1.5">
          <span className="inline-flex min-w-0 shrink items-center break-words rounded-full bg-[#040033]/[0.06] px-2.5 py-1 font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#040033]">
            {city}
          </span>
          {/* A restaurant table is free to reserve, so it carries no package and
              no price. Rendering it anyway printed a meaningless "MAD 0". */}
          {hasPrice && (
            <div className="flex shrink-0 items-center gap-[5px] whitespace-nowrap text-[11px] font-extrabold leading-[1.3] text-[#040033]">
              <span>
                {display.value} {display.currency}
              </span>
              <span className="h-3 w-px bg-[#040033]/20" />
              <Icon name={priceIcon} className={`text-[12px] text-[#040033]/60`} aria-hidden="true"
                title={priceLabel} />
            </div>
          )}
        </div>
        {showBookedCta && bookingHref ? (
          <a
            href={bookingHref}
            aria-label={t("booking_sheet.view_booking", { defaultValue: "View my booking" })}
            className="mt-1.5 flex min-h-[38px] w-full items-center justify-between gap-2 rounded-full bg-gradient-to-r from-[#110E39] to-[#241d6e] px-2 ps-2.5 text-white no-underline shadow-[0_8px_18px_rgba(17,14,57,0.25)] transition-opacity duration-200 hover:opacity-95"
          >
            <span className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#E5B265]">
                <Icon name="ri-check-line" className="text-[10px] text-[#110E39]" aria-hidden />
              </span>
              <span className="leading-tight">
                <span className="block text-[8.5px] font-bold uppercase tracking-[0.12em] text-[#E5B265]">
                  {t("activity.timeline_awaiting", { defaultValue: "Awaiting" })}
                </span>
                <span className="block font-mono text-[11px] text-white/90">{shortRef}</span>
              </span>
            </span>
            <Icon name="ri-arrow-right-line" className="text-[14px] rtl:-scale-x-100" aria-hidden />
          </a>
        ) : (
          <a
            href={href}
            className={cn(
              "mt-1.5 flex min-h-[38px] w-full items-center justify-center rounded-full px-3.5 font-sans text-[12px] font-bold tracking-[0.02em] no-underline",
              variant !== "default" && "gap-1.5 transition-opacity duration-200 hover:opacity-90",
              variant === "wishlist" && "mj-book-cta text-white",
              variant === "blog" && "bg-[#040033]/[0.06] text-[#040033]",
              // Gradient + hover motion live in .mj-book-cta (tailwind.css).
              variant === "default" &&
                "mj-book-cta text-white shadow-[0_8px_24px_-8px_rgba(4,0,51,0.55),inset_0_1.5px_0_rgba(255,255,255,0.22),inset_0_-1.5px_0_rgba(0,0,0,0.25)]",
            )}
          >
            {variant === "default" ? (
              <>
                <span className="mj-book-cta-label">{ctaLabel}</span>
                <Icon name="ri-arrow-right-line" className="mj-book-cta-arrow text-[14px]" aria-hidden />
              </>
            ) : (
              ctaLabel
            )}
          </a>
        )}
      </div>
      </div>
    </article>
  );
}

function getPriceIcon(category?: string, priceType?: string | number): string {
  const c = (category ?? "").toLowerCase();
  const p = String(priceType ?? "").toLowerCase();
  if (priceType === 3) return "ri-group-line";
  if (priceType === 2) return "ri-hotel-bed-line";
  if (priceType === 1) return "ri-time-line";
  if (priceType === 0) return "ri-user-line";
  if (/group|flat/.test(p)) return "ri-group-line";
  if (/night|room|stay/.test(p)) return "ri-hotel-bed-line";
  if (/hour/.test(p)) return "ri-time-line";
  if (/(stay|hotel|riad|room|lodge|camp|villa)/.test(c)) return "ri-hotel-bed-line";
  return "ri-user-line";
}
